import { apiClient, ApiResponse } from './apiClient';

export interface SystemSetting {
  id: string;
  key: string;
  value: string | null;
  group: string;
  description: string;
  is_encrypted: boolean;
  is_active: boolean;
  updated_by: string | null;
  createdAt: string;
  updatedAt: string;
  Updater: any | null;
}

export interface SystemSettingsResponse {
  email?: SystemSetting[];
  firebase?: SystemSetting[];
  general?: SystemSetting[];
  sms?: SystemSetting[];
}

export interface UpdateSettingRequest {
  key: string;
  value: string;
  group: string;
}

export interface BulkUpdateSettingsRequest {
  settings: {
    key: string;
    value: string;
    is_active?: boolean;
  }[];
}

export interface CreateSettingRequest {
  key: string;
  value: string;
  group: 'sms' | 'firebase' | 'email' | 'general';
  description?: string;
  is_encrypted?: boolean;
}

export interface SettingsGroup {
  [key: string]: string | null;
}

export class SettingsService {
  private baseUrl = '/admin/settings';

  // Get all system settings
  async getSystemSettings(): Promise<ApiResponse<SystemSettingsResponse>> {
    try {
      console.log('🔧 SettingsService.getSystemSettings called');
      console.log('🔧 Making GET request to:', this.baseUrl);
      
      const response = await apiClient.get<SystemSettingsResponse>(this.baseUrl);
      
      console.log('🔧 SettingsService.getSystemSettings response:', response);
      console.log('🔧 Response success:', response.success);
      console.log('🔧 Response data:', response.data);
      
      return response;
    } catch (error: any) {
      console.error('🔧 SettingsService.getSystemSettings error:', error);
      console.error('🔧 Error status:', error.response?.status);
      console.error('🔧 Error data:', error.response?.data);
      
      let errorMessage = 'Error fetching system settings';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error - Settings endpoint may not be implemented yet';
        console.warn('🔧 500 Error: Settings endpoint appears to be not implemented on the backend');
      } else if (error.response?.status === 404) {
        errorMessage = 'Settings endpoint not found';
        console.warn('🔧 404 Error: Settings endpoint not found');
      } else if (error.response?.status === 401) {
        errorMessage = 'Unauthorized access to settings';
      } else if (error.response?.status === 403) {
        errorMessage = 'Forbidden access to settings';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: {
          email: [],
          firebase: [],
          general: [],
          sms: []
        },
        message: errorMessage
      };
    }
  }

  // Get settings by group
  async getSettingsByGroup(group: string): Promise<ApiResponse<SystemSetting[]>> {
    try {
      console.log('🔧 SettingsService.getSettingsByGroup called:', { group });
      console.log('🔧 Making GET request to:', `${this.baseUrl}/${group}`);
      
      const response = await apiClient.get<SystemSetting[]>(`${this.baseUrl}/${group}`);
      
      console.log('🔧 SettingsService.getSettingsByGroup response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 SettingsService.getSettingsByGroup error:', error);
      
      let errorMessage = `Error fetching ${group} settings`;
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: [],
        message: errorMessage
      };
    }
  }

  // Update a setting
  async updateSetting(data: UpdateSettingRequest): Promise<ApiResponse<any>> {
    try {
      console.log('🔧 SettingsService.updateSetting called:', { data });
      console.log('🔧 Making PUT request to:', this.baseUrl);
      
      const requestData = {
        settings: [
          {
            key: data.key,
            value: data.value,
            is_active: true
          }
        ]
      };
      
      console.log('🔧 Request payload:', requestData);
      
      const response = await apiClient.put<any>(this.baseUrl, requestData);
      
      console.log('🔧 SettingsService.updateSetting response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 SettingsService.updateSetting error:', error);
      console.error('🔧 Error status:', error.response?.status);
      console.error('🔧 Error data:', error.response?.data);
      
      let errorMessage = 'Error updating setting';
      
      if (error.response?.status === 422) {
        errorMessage = 'Validation error - Please check your input data';
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const validationErrors = error.response.data.errors.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err.msg && err.path) return `${err.path}: ${err.msg}`;
            return err.msg || err.message || 'Validation error';
          });
          errorMessage = validationErrors.join(', ');
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 404) {
        errorMessage = 'Setting not found';
      } else if (error.response?.status === 401) {
        errorMessage = 'Unauthorized access to update settings';
      } else if (error.response?.status === 403) {
        errorMessage = 'Forbidden access to update settings';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Delete a setting
  async deleteSetting(key: string, group: string): Promise<ApiResponse<any>> {
    try {
      console.log('🔧 SettingsService.deleteSetting called:', { key, group });
      console.log('🔧 Making DELETE request to:', `${this.baseUrl}/${group}/${key}`);
      
      const response = await apiClient.delete<any>(`${this.baseUrl}/${group}/${key}`);
      
      console.log('🔧 SettingsService.deleteSetting response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 SettingsService.deleteSetting error:', error);
      console.error('🔧 Error status:', error.response?.status);
      console.error('🔧 Error data:', error.response?.data);
      
      let errorMessage = 'Error deleting setting';
      
      if (error.response?.status === 404) {
        errorMessage = 'Setting not found';
      } else if (error.response?.status === 401) {
        errorMessage = 'Unauthorized access to delete settings';
      } else if (error.response?.status === 403) {
        errorMessage = 'Forbidden access to delete settings';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Bulk update settings for a group
  async updateGroupSettings(group: string, settings: { [key: string]: string }): Promise<ApiResponse<SystemSetting[]>> {
    try {
      console.log('🔧 SettingsService.updateGroupSettings called:', { group, settings });
      console.log('🔧 Making PUT request to:', `${this.baseUrl}/${group}`);
      
      const response = await apiClient.put<SystemSetting[]>(`${this.baseUrl}/${group}`, settings);
      
      console.log('🔧 SettingsService.updateGroupSettings response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 SettingsService.updateGroupSettings error:', error);
      
      let errorMessage = `Error updating ${group} settings`;
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: [],
        message: errorMessage
      };
    }
  }

  // Create a new setting
  async createSetting(data: CreateSettingRequest): Promise<ApiResponse<SystemSetting>> {
    try {
      console.log('🔧 SettingsService.createSetting called:', { data });
      console.log('🔧 Making POST request to:', this.baseUrl);
      console.log('🔧 Request payload:', JSON.stringify(data, null, 2));
      
      const response = await apiClient.post<SystemSetting>(this.baseUrl, data);
      
      console.log('🔧 SettingsService.createSetting response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 SettingsService.createSetting error:', error);
      console.error('🔧 Error status:', error.response?.status);
      console.error('🔧 Error data:', error.response?.data);
      
      let errorMessage = 'Error creating setting';
      
      if (error.response?.status === 422) {
        errorMessage = 'Validation error - Please check your input data';
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const validationErrors = error.response.data.errors.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err.msg && err.path) return `${err.path}: ${err.msg}`;
            return err.msg || err.message || 'Validation error';
          });
          errorMessage = validationErrors.join(', ');
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 409) {
        errorMessage = 'Setting with this key already exists';
      } else if (error.response?.status === 404) {
        errorMessage = 'Settings endpoint not found';
      } else if (error.response?.status === 401) {
        errorMessage = 'Unauthorized access to create settings';
      } else if (error.response?.status === 403) {
        errorMessage = 'Forbidden access to create settings';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Bulk update settings using new API structure
  async bulkUpdateSettings(data: BulkUpdateSettingsRequest): Promise<ApiResponse<SystemSetting[]>> {
    try {
      console.log('🔧 SettingsService.bulkUpdateSettings called:', { data });
      console.log('🔧 Making PUT request to:', this.baseUrl);
      console.log('🔧 Request payload:', JSON.stringify(data, null, 2));
      
      const response = await apiClient.put<SystemSetting[]>(this.baseUrl, data);
      
      console.log('🔧 SettingsService.bulkUpdateSettings response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 SettingsService.bulkUpdateSettings error:', error);
      console.error('🔧 Error status:', error.response?.status);
      console.error('🔧 Error data:', error.response?.data);
      
      let errorMessage = 'Error updating settings';
      
      if (error.response?.status === 422) {
        errorMessage = 'Validation error - Please check your input data';
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const validationErrors = error.response.data.errors.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err.msg && err.path) return `${err.path}: ${err.msg}`;
            return err.msg || err.message || 'Validation error';
          });
          errorMessage = validationErrors.join(', ');
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 404) {
        errorMessage = 'Settings endpoint not found';
      } else if (error.response?.status === 401) {
        errorMessage = 'Unauthorized access to update settings';
      } else if (error.response?.status === 403) {
        errorMessage = 'Forbidden access to update settings';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: [],
        message: errorMessage
      };
    }
  }

  // Helper function to convert settings array to key-value object
  settingsArrayToObject(settings: SystemSetting[] | undefined | null): SettingsGroup {
    const result: SettingsGroup = {};
    
    // Check if settings is defined and is an array
    if (!settings || !Array.isArray(settings)) {
      return result;
    }
    
    settings.forEach(setting => {
      if (setting && setting.key) {
        result[setting.key] = setting.value;
      }
    });
    return result;
  }

  // Helper function to get a specific setting value
  getSettingValue(settings: SystemSetting[] | undefined | null, key: string): string | null {
    if (!settings || !Array.isArray(settings)) {
      return null;
    }
    const setting = settings.find(s => s.key === key);
    return setting ? setting.value : null;
  }

  // Helper function to check if a setting is enabled (for boolean settings)
  isSettingEnabled(settings: SystemSetting[] | undefined | null, key: string): boolean {
    const value = this.getSettingValue(settings, key);
    return value === 'true' || value === '1';
  }
}

export const settingsService = new SettingsService();